import { Text, Container } from 'pixi.js';

interface ActivePopup {
  text: Text;
  lifetime: number;
  maxLifetime: number;
  startY: number;
}

/**
 * Floating number popups above characters when they contribute to a project.
 * "+5 Fun", "+3 Code" etc — floats up and fades out.
 */
export class NumberPopupManager {
  private container: Container;
  private popups: ActivePopup[] = [];
  private maxVisible: number = 8;

  constructor(parent: Container) {
    this.container = new Container();
    this.container.zIndex = 100;
    parent.addChild(this.container);
  }

  /**
   * Show a number popup at the given screen position.
   */
  spawn(x: number, y: number, value: string, color: number = 0xffffff): void {
    if (this.popups.length >= this.maxVisible) {
      // Remove oldest
      const oldest = this.popups.shift();
      if (oldest) {
        this.container.removeChild(oldest.text);
        oldest.text.destroy();
      }
    }

    const text = new Text({
      text: value,
      style: {
        fontSize: 10,
        fill: color,
        fontFamily: 'monospace',
        fontWeight: 'bold',
        stroke: { color: 0x000000, width: 2 },
      },
    });
    text.anchor.set(0.5, 1);
    text.position.set(x, y);

    this.container.addChild(text);
    this.popups.push({
      text,
      lifetime: 0,
      maxLifetime: 60, // ~1 second at 60fps
      startY: y,
    });
  }

  /** Update all popups — float up and fade out. */
  update(): void {
    const toRemove: number[] = [];

    for (let i = 0; i < this.popups.length; i++) {
      const popup = this.popups[i]!;
      popup.lifetime++;

      const progress = popup.lifetime / popup.maxLifetime;
      popup.text.position.y = popup.startY - progress * 20;
      popup.text.alpha = 1 - progress;

      if (popup.lifetime >= popup.maxLifetime) {
        toRemove.push(i);
      }
    }

    // Remove expired (reverse order to maintain indices)
    for (let i = toRemove.length - 1; i >= 0; i--) {
      const idx = toRemove[i]!;
      const popup = this.popups[idx]!;
      this.container.removeChild(popup.text);
      popup.text.destroy();
      this.popups.splice(idx, 1);
    }
  }

  /** Clear all popups. */
  clear(): void {
    for (const popup of this.popups) {
      this.container.removeChild(popup.text);
      popup.text.destroy();
    }
    this.popups = [];
  }

  destroy(): void {
    this.clear();
    this.container.destroy();
  }
}
